// src/pages/WHSManagementPlan.tsx — G-01: WHS Management Plan module
// HRCW notification, WHSMP lifecycle, section-by-section completion,
// PDF export with platform branding. WHS Regulation 2011 (Qld) s.309.

import { useState } from 'react'
import { useAuthStore } from '../stores/auth.store'
import { MOCK_WHS_PLANS, WHSManagementPlan as WHSMPType, WHSMPStatus, MOCK_COMPLIANCE_REGISTER, getWHSDocumentStyle, PLAN_SECTIONS } from '../utils/mockDataWHSMgmt'
import { MOCK_JOBS } from '../utils/mockData'
import { Shield, Plus, CheckCircle, AlertTriangle, X, ChevronRight, FileText, Download, Lock, Clock, Sparkles } from 'lucide-react'
import { buildDocumentHeader, buildDocumentFooter } from '../utils/documentBranding'

const fmtDate = (s?: string|null) => s ? new Date(s).toLocaleDateString('en-AU',{day:'numeric',month:'short',year:'2-digit'}) : '—'

// ─── PDF generation ─────────────────────────────────────────────────────

function generateWHSMPPDF(plan: WHSMPType) {
  const now     = new Date().toLocaleDateString('en-AU', { day:'2-digit', month:'long', year:'numeric' })
  const pct     = Math.round(plan.sections.filter(s=>s.complete).length / plan.sections.length * 100)
  const complete = plan.sections.every(s => s.complete)

  const sectionHtml = plan.sections.map((s, i) => `
    <div class="section">
      <div class="sec-hdr">${i+1}.&nbsp;&nbsp;${s.title}</div>
      <div class="sec-leg">${s.requirement}</div>
      ${s.complete
        ? `<div class="sec-body">${s.content.replace(/\n/g,'<br>')}</div>
           <div class="sec-meta">Completed by ${s.updatedByName} &nbsp;&bull;&nbsp; ${s.lastUpdated}</div>`
        : `<div class="sec-empty">⚠️&nbsp; Section incomplete — must be completed before plan is approved.</div>`
      }
    </div>`
  ).join('')

  const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>WHS Management Plan — ${plan.planNumber}</title>
<style>
*{box-sizing:border-box}
body{font-family:Arial,sans-serif;font-size:10.5pt;color:#111;margin:20mm 22mm}
.doc-title{border-bottom:3px solid #2E75B6;padding-bottom:8px;margin-bottom:14px}
h2{font-size:15pt;font-weight:900;color:#1F3864;margin:0 0 2px;text-transform:uppercase;letter-spacing:.5px}
.subtitle{font-size:9.5pt;color:#555}
.meta-grid{display:grid;grid-template-columns:1fr 1fr;gap:0;border:1px solid #ccc;margin:14px 0}
.mc{padding:6px 10px;border-bottom:1px solid #eee;font-size:9pt}
.mc:nth-child(odd){background:#f7f8fa;font-weight:bold;color:#555}
.hrcw-box{background:#FFF3E0;border:1.5px solid #F57C00;border-radius:4px;padding:10px 14px;margin:12px 0 18px;font-size:9pt}
.hrcw-box strong{color:#E65100}
.section{margin-bottom:18px;break-inside:avoid}
.sec-hdr{font-size:11pt;font-weight:bold;color:#1F3864;border-left:4px solid #2E75B6;padding-left:8px;margin-bottom:3px}
.sec-leg{font-size:8.5pt;color:#888;font-style:italic;margin-bottom:6px}
.sec-body{background:#f9fafb;border:1px solid #e0e0e0;border-radius:3px;padding:10px 12px;font-size:10pt;line-height:1.55;white-space:pre-wrap}
.sec-meta{font-size:8pt;color:#aaa;margin-top:4px;text-align:right}
.sec-empty{background:#FFF8E1;border:1.5px dashed #F9A825;border-radius:3px;padding:10px 12px;font-size:9.5pt;color:#B45309}
.progress-bar{height:8px;background:#e0e0e0;border-radius:4px;margin:6px 0}
.progress-fill{height:100%;border-radius:4px;background:${pct===100?'#4CAF50':pct>50?'#FFA726':'#EF5350'}}
.sig-grid{display:grid;grid-template-columns:1fr 1fr;gap:24px;margin-top:24px}
.sig-box{border:1.5px solid #999;border-radius:4px;padding:12px 14px;min-height:80px}
.sig-lbl{font-size:8.5pt;font-weight:bold;color:#555;text-transform:uppercase;margin-bottom:4px}
.sig-line{border-bottom:1px solid #555;height:28px;margin:8px 0 4px}
.sig-name{font-size:8pt;color:#777}
@media print{body{margin:15mm}.section{break-inside:avoid}}
</style></head><body>
${buildDocumentHeader('INVOICE')}
<div class="doc-title">
  <h2>WHS Management Plan</h2>
  <div class="subtitle">WHS Regulation 2011 (Qld) s.309 &mdash; Required before high risk construction work commences</div>
</div>

<div class="meta-grid">
  <div class="mc">Plan number</div>   <div class="mc">${plan.planNumber}</div>
  <div class="mc">Job number</div>    <div class="mc">${plan.jobNumber}</div>
  <div class="mc">Version</div>       <div class="mc">${plan.version}.0</div>
  <div class="mc">Status</div>        <div class="mc">${plan.status}</div>
  <div class="mc">Prepared by</div>   <div class="mc">${plan.preparedByName} &mdash; ${fmtDate(plan.preparedAt)}</div>
  <div class="mc">Approved by</div>   <div class="mc">${plan.approvedByName ?? 'Pending approval'} ${plan.approvedAt ? '&mdash; ' + fmtDate(plan.approvedAt) : ''}</div>
  <div class="mc">Review date</div>   <div class="mc">${fmtDate(plan.reviewDate)}</div>
  <div class="mc">Completion</div>    <div class="mc">
    <div class="progress-bar"><div class="progress-fill" style="width:${pct}%"></div></div>
    ${pct}% &mdash; ${plan.sections.filter(s=>s.complete).length} of ${plan.sections.length} sections
  </div>
</div>

<div class="hrcw-box">
  <strong>High Risk Construction Work (HRCW) categories identified:</strong><br>
  ${plan.hrcwCategories.join(' &nbsp;&bull;&nbsp; ')}
</div>

${sectionHtml}

<div class="sig-grid">
  <div class="sig-box">
    <div class="sig-lbl">Prepared by</div>
    <div class="sig-line">${plan.preparedByName ? '✓ Signed electronically' : ''}</div>
    <div class="sig-name">${plan.preparedByName}</div>
    <div class="sig-name" style="margin-top:4px">Date: ${fmtDate(plan.preparedAt)}</div>
  </div>
  <div class="sig-box">
    <div class="sig-lbl">Approved by (PM)</div>
    <div class="sig-line">${plan.approvedByName ? '✓ Signed electronically' : ''}</div>
    <div class="sig-name">${plan.approvedByName ?? 'Pending approval'}</div>
    <div class="sig-name" style="margin-top:4px">Date: ${plan.approvedAt ? fmtDate(plan.approvedAt) : '______ / ______ / ______'}</div>
  </div>
</div>

${buildDocumentFooter('INVOICE', plan.planNumber)}
</body></html>`

  const w = window.open('', '_blank', 'width=1000,height=820')
  if (!w) { alert('Pop-up blocked — allow pop-ups to generate PDFs'); return }
  w.document.write(html)
  w.document.close()
  setTimeout(() => { w.focus(); w.print() }, 350)
}

const STATUS_META: Record<WHSMPStatus,{label:string;bg:string;text:string;dot:string}> = {
  DRAFT:        { label:'Draft',          bg:'bg-gray-100',  text:'text-gray-600',  dot:'bg-gray-400' },
  UNDER_REVIEW: { label:'Under review',   bg:'bg-amber-100', text:'text-amber-700', dot:'bg-amber-500' },
  APPROVED:     { label:'Approved',       bg:'bg-green-100', text:'text-green-700', dot:'bg-green-500' },
  SUPERSEDED:   { label:'Superseded',     bg:'bg-gray-200',  text:'text-gray-500',  dot:'bg-gray-400' },
}

function WHSMPDetail({ plan, onClose, canApprove, onSubmit, onApprove, onReject }: { plan: WHSMPType; onClose: ()=>void; canApprove: boolean; onSubmit: ()=>void; onApprove: ()=>void; onReject: ()=>void }) {
  const [sections, setSections] = useState(plan.sections)
  const [activeSection, setActiveSection] = useState(sections[0]?.id ?? '')
  const [editContent, setEditContent] = useState('')
  const [editing, setEditing] = useState(false)
  const { user } = useAuthStore()
  const style = getWHSDocumentStyle()

  const completePct = Math.round(sections.filter(s=>s.complete).length / sections.length * 100)
  const selectedSec = sections.find(s => s.id === activeSection)

  const saveSection = () => {
    setSections(p => p.map(s => s.id === activeSection ? { ...s, content: editContent, complete: editContent.trim().length > 20, lastUpdated: new Date().toISOString().split('T')[0], updatedByName: user?.fullName ?? 'User' } : s))
    setEditing(false)
  }

  return (
    <div className="flex flex-col h-full bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b bg-gray-50 flex-shrink-0">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-mono text-gray-400">{plan.planNumber}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_META[plan.status].bg} ${STATUS_META[plan.status].text}`}>{STATUS_META[plan.status].label}</span>
              <span className="text-xs text-gray-500">v{plan.version}</span>
            </div>
            <div className="font-semibold text-gray-900">{plan.jobNumber} — WHS Management Plan</div>
            <div className="text-xs text-gray-500 mt-0.5">{completePct}% complete · Review: {fmtDate(plan.reviewDate)}</div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => generateWHSMPPDF(plan)} className="btn-secondary text-xs flex items-center gap-1"><Download className="w-3.5 h-3.5"/>Export PDF</button>
            <button onClick={onClose}><X className="w-4 h-4 text-gray-400"/></button>
          </div>
        </div>
        {/* Progress bar */}
        <div className="mt-3">
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all ${completePct===100?'bg-green-500':completePct>50?'bg-amber-400':'bg-red-400'}`} style={{width:`${completePct}%`}}/>
          </div>
          <div className="flex justify-between text-[10px] text-gray-400 mt-0.5"><span>{sections.filter(s=>s.complete).length} of {sections.length} sections complete</span><span>{completePct < 100 ? `${sections.filter(s=>!s.complete).length} outstanding` : 'All sections complete ✓'}</span></div>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Section list */}
        <div className="w-56 flex-shrink-0 border-r overflow-y-auto">
          {sections.map((s, i) => (
            <button key={s.id} onClick={()=>{setActiveSection(s.id);setEditing(false)}} className={`w-full text-left px-3 py-2.5 border-b flex items-center gap-2 transition-colors ${activeSection===s.id?'bg-blue-50 border-l-2 border-l-[#2E75B6]':'hover:bg-gray-50'}`}>
              <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold ${s.complete?'bg-green-500 text-white':'bg-gray-200 text-gray-500'}`}>{s.complete?'✓':i+1}</div>
              <div className="flex-1 min-w-0"><div className={`text-xs font-medium truncate ${activeSection===s.id?'text-[#2E75B6]':'text-gray-700'}`}>{s.title}</div><div className="text-[10px] text-gray-400 truncate">{s.requirement}</div></div>
            </button>
          ))}
        </div>

        {/* Section content */}
        {selectedSec && (
          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div><h3 className="font-semibold text-gray-900 text-sm">{selectedSec.title}</h3><div className="text-xs text-gray-500 mt-0.5 flex items-center gap-1"><Shield className="w-3 h-3"/>{selectedSec.requirement}</div></div>
              {!editing && <button onClick={()=>{setEditContent(selectedSec.content);setEditing(true)}} className="btn-secondary text-xs">Edit section</button>}
            </div>

            {!editing ? (
              selectedSec.complete
                ? <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{selectedSec.content}<div className="text-xs text-gray-400 mt-3">Last updated: {fmtDate(selectedSec.lastUpdated)} by {selectedSec.updatedByName}</div></div>
                : <div className="bg-amber-50 border border-amber-200 border-dashed rounded-xl p-6 text-center"><AlertTriangle className="w-6 h-6 text-amber-400 mx-auto mb-2"/><div className="text-sm font-medium text-amber-700">Section incomplete</div><div className="text-xs text-amber-600 mt-0.5 mb-3">This section is required for the WHSMP to be approved.</div><button onClick={()=>{setEditContent('');setEditing(true)}} className="btn-primary text-xs">Complete this section</button></div>
            ) : (
              <div className="space-y-3">
                <textarea className="input text-sm w-full h-48 resize-none" value={editContent} onChange={e=>setEditContent(e.target.value)} placeholder="Describe how this requirement is addressed for this project…"/>
                <div className="flex items-center gap-1.5 text-xs text-gray-500"><Sparkles className="w-3 h-3 text-[#2E75B6]"/><span>WHS AI Agent (planned) will suggest content based on SWMS, permit, and drawing data.</span></div>
                <div className="flex gap-2"><button onClick={()=>setEditing(false)} className="btn-secondary text-xs">Cancel</button><button onClick={saveSection} disabled={editContent.trim().length<20} className="btn-primary text-xs disabled:opacity-50">Save section</button></div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Approval footer */}
      {completePct === 100 && plan.status === 'DRAFT' && canApprove && (
        <div className="border-t px-5 py-3 flex gap-2 flex-shrink-0 bg-green-50">
          <div className="flex-1 text-xs text-green-800"><CheckCircle className="w-3.5 h-3.5 inline mr-1"/>All sections complete. Submit for PM approval.</div>
          <button onClick={onSubmit} className="btn-primary text-xs">Submit for approval</button>
        </div>
      )}
      {plan.status === 'UNDER_REVIEW' && canApprove && (
        <div className="border-t px-5 py-3 flex gap-2 flex-shrink-0 bg-amber-50">
          <div className="flex-1 text-xs text-amber-800">Ready for PM approval. This plan is required on site before HRCW commences (WHS Reg 2011 s.309).</div>
          <button onClick={onReject} className="btn-secondary text-xs">Reject</button>
          <button onClick={onApprove} className="btn-primary text-xs flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5"/>Approve WHSMP</button>
        </div>
      )}
    </div>
  )
}

export function WHSManagementPlan() {
  const { isTier, user } = useAuthStore()
  const canApprove = isTier('B_PM') || isTier('A_DEVELOPER')
  const canEdit    = isTier('B_PC') || canApprove
  const [plans, setPlans]         = useState(MOCK_WHS_PLANS)
  const [selected, setSelected]   = useState<WHSMPType|null>(null)
  const [showNew, setShowNew]     = useState(false)
  const [newJobId, setNewJobId]   = useState(MOCK_JOBS[0]?.id ?? '')
  const [selectedHrcw, setSelectedHrcw] = useState<string[]>(['Energised electrical', 'Traffic vicinity'])

  const activeJobs = MOCK_JOBS.filter(j => !['COMPLETED','CLOSED'].includes(j.status))
  const jobsWithoutPlan = activeJobs.filter(j => !plans.some(p => p.jobId === j.id))
  const pending = plans.filter(p => p.status === 'UNDER_REVIEW').length

  // ── State handlers ────────────────────────────────────────────
  const updatePlanStatus = (id: string, patch: Partial<WHSMPType>) =>
    setPlans(prev => prev.map(p => p.id === id ? { ...p, ...patch } : p))

  const handleSubmit = (id: string) => updatePlanStatus(id, { status: 'UNDER_REVIEW' })
  const handleApprove = (id: string) => {
    updatePlanStatus(id, { status: 'APPROVED', approvedByName: user?.fullName ?? 'PM', approvedAt: new Date().toISOString().split('T')[0] })
    setSelected(prev => prev ? { ...prev, status: 'APPROVED', approvedByName: user?.fullName ?? 'PM', approvedAt: new Date().toISOString().split('T')[0] } : null)
  }
  const handleReject = (id: string) => updatePlanStatus(id, { status: 'DRAFT' })

  const handleCreate = () => {
    const job = activeJobs.find(j => j.id === newJobId)
    if (!job) return
    const newPlan: WHSMPType = {
      id:            `whsmp-${Date.now()}`,
      planNumber:    `WHSMP-${job.jobNumber.replace('TP-','')}-001`,
      jobId:         job.id,
      jobNumber:     job.jobNumber,
      version:       1,
      status:        'DRAFT',
      preparedById:  user?.id ?? 'u2',
      preparedByName: user?.fullName ?? 'PM',
      preparedAt:    new Date().toISOString().split('T')[0],
      approvedById:  null, approvedByName: null, approvedAt: null,
      reviewDate:    new Date(Date.now() + 365*86400000).toISOString().split('T')[0],
      hrcwCategories: selectedHrcw.length ? selectedHrcw : ['Energised electrical'],
      sections:      PLAN_SECTIONS.map(s => ({ ...s, content: '', complete: false, lastUpdated: '', updatedByName: '' })),
    }
    setPlans(prev => [...prev, newPlan])
    setSelected(newPlan)
    setShowNew(false)
  }

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Shield className="w-6 h-6 text-[#2E75B6]"/>WHS Management Plans</h1>
          <p className="text-sm text-gray-500 mt-0.5">Required for all HRCW projects › WHS Regulation 2011 (Qld) s.309 › v{plans.length > 0 ? Math.max(...plans.map(p=>p.version)) : 1}.0</p>
        </div>
        {canEdit && <button onClick={()=>setShowNew(true)} className="btn-primary text-sm flex items-center gap-2"><Plus className="w-4 h-4"/>New WHSMP</button>}
      </div>

      {jobsWithoutPlan.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-3 text-sm text-red-800">
          <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0"/>
          <span><strong>{jobsWithoutPlan.length} active job{jobsWithoutPlan.length!==1?'s':''}</strong> ({jobsWithoutPlan.map(j=>j.jobNumber).join(', ')}) do not have a WHS Management Plan. If these are HRCW projects, this is a Category 1 legislative breach.</span>
        </div>
      )}
      {pending > 0 && canApprove && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center gap-3 text-sm text-amber-800">
          <Clock className="w-4 h-4 text-amber-600 flex-shrink-0"/>
          <span><strong>{pending} WHSMP{pending!==1?'s':''} pending your approval.</strong> Site work cannot be classified as HRCW-compliant until the plan is approved.</span>
        </div>
      )}

      {showNew && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between"><h4 className="font-semibold text-blue-900">New WHS Management Plan</h4><button onClick={()=>setShowNew(false)}><X className="w-4 h-4 text-gray-400"/></button></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Job</label><select className="input text-sm" value={newJobId} onChange={e=>setNewJobId(e.target.value)}>{activeJobs.map(j=><option key={j.id} value={j.id}>{j.jobNumber} — {j.description.slice(0,40)}</option>)}</select></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">HRCW categories (select all that apply)</label><select className="input text-sm" multiple value={selectedHrcw} onChange={e=>setSelectedHrcw(Array.from(e.target.selectedOptions,o=>o.value))}><option>Energised electrical</option><option>Traffic vicinity</option><option>Confined space</option><option>Excavation &gt;1.5m</option><option>Working at height</option></select></div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800"><AlertTriangle className="w-3 h-3 inline mr-1"/>A new WHSMP will be created with all 10 required sections. Complete all sections before PM approval. The approved WHSMP must be available on site at all times during HRCW.</div>
          <div className="flex justify-end gap-2"><button onClick={()=>setShowNew(false)} className="btn-secondary text-sm">Cancel</button><button onClick={handleCreate} className="btn-primary text-sm">Create WHSMP</button></div>
        </div>
      )}

      <div className="grid grid-cols-4 gap-3">
        {[
          { label:'Total WHSMPs',   value:plans.length,                                       color:'text-gray-900',  sub:'all jobs' },
          { label:'Approved',       value:plans.filter(p=>p.status==='APPROVED').length,       color:'text-green-700', sub:'compliant' },
          { label:'Pending approval',value:pending,                                            color:pending>0?'text-amber-700':'text-gray-400', sub:'awaiting PM' },
          { label:'Jobs without plan',value:jobsWithoutPlan.length,                           color:jobsWithoutPlan.length>0?'text-red-600':'text-gray-400', sub:'action required' },
        ].map(({label,value,color,sub})=>(
          <div key={label} className="bg-white border border-gray-200 rounded-xl px-4 py-3">
            <div className={`text-2xl font-bold ${color}`}>{value}</div>
            <div className="text-xs font-medium text-gray-700 mt-0.5">{label}</div>
            <div className="text-xs text-gray-400">{sub}</div>
          </div>
        ))}
      </div>

      <div className="flex gap-5 items-start">
        <div className={`${selected?'w-1/2':'w-full'} space-y-2 transition-all`}>
          {plans.map(plan => {
            const sm = STATUS_META[plan.status]; const isSel = selected?.id === plan.id
            const completePct = Math.round(plan.sections.filter(s=>s.complete).length / plan.sections.length * 100)
            return (
              <div key={plan.id} onClick={()=>setSelected(isSel?null:plan)} className={`bg-white border rounded-xl p-4 cursor-pointer transition-all hover:shadow-sm ${isSel?'border-blue-400 ring-2 ring-blue-100':plan.status==='UNDER_REVIEW'?'border-amber-300':completePct<100?'border-orange-200':'border-gray-200 hover:border-gray-300'}`}>
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-xs font-mono text-gray-400">{plan.planNumber}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sm.bg} ${sm.text}`}>{sm.label}</span>
                      <span className="text-xs text-gray-500">v{plan.version}</span>
                    </div>
                    <div className="font-semibold text-gray-900">{plan.jobNumber} — WHS Management Plan</div>
                    <div className="flex items-center gap-4 mt-1.5 text-xs text-gray-500">
                      <span>Review: {fmtDate(plan.reviewDate)}</span>
                      <span>{plan.hrcwCategories.length} HRCW categories</span>
                      <span className={completePct===100?'text-green-600 font-medium':completePct<50?'text-red-600 font-medium':'text-amber-600 font-medium'}>{completePct}% complete</span>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mt-2"><div className={`h-full rounded-full ${completePct===100?'bg-green-500':completePct>50?'bg-amber-400':'bg-red-400'}`} style={{width:`${completePct}%`}}/></div>
                  </div>
                  <ChevronRight className={`w-4 h-4 flex-shrink-0 mt-1 ${isSel?'text-blue-500':'text-gray-400'}`}/>
                </div>
              </div>
            )
          })}
          {plans.length===0 && <div className="bg-white border border-dashed border-gray-300 rounded-xl py-12 text-center text-gray-400 text-sm">No WHSMP records — create one for each HRCW project</div>}
        </div>
        {selected && (
          <div className="w-1/2 sticky top-6" style={{maxHeight:'calc(100vh - 160px)'}}>
            <WHSMPDetail plan={selected} onClose={()=>setSelected(null)} canApprove={canApprove} onSubmit={()=>handleSubmit(selected.id)} onApprove={()=>handleApprove(selected.id)} onReject={()=>handleReject(selected.id)}/>
          </div>
        )}
      </div>
    </div>
  )
}
